import { IError } from '@checkout/errors';
import { ISelection } from '@checkout/items';
import { INotification } from '@checkout/notifications';
import {
  IAccountChangeTabPayload,
  IAccountDetailsChangeRequestPayload,
  IAccountDetailsChangeSuccessPayload,
  IAddToCartRequestPayload,
  IAddToCartSuccessPayload,
  IAddVoucherSuccessPayload,
  ICampaignSiteChangeRequestPayload,
  ICampaignSiteChangeSuccessPayload,
  IChangePaymentMethodRequestPayload,
  IChangePaymentMethodSuccessPayload,
  IChangeShippingMethodRequestPayload,
  IChangeShippingMethodSuccessPayload,
  IChangeStepPayload,
  ICountryChangeRequestPayload,
  ICountryChangeSuccessPayload,
  ICountryStateChangePayload,
  IFetchWidgetSuccessPayload,
  ILanguageChangeRequestPayload,
  ILanguageChangeSuccessPayload,
  ILineItemAddRequestPayload,
  ILineItemAddSuccessPayload,
  ILineItemRemoveRequestPayload,
  ILineItemRemoveSuccessPayload,
  ILogInRequestPayload,
  ILogInSuccessPayload,
  ILogOutSuccessPayload,
  IMarketChangeRequestPayload,
  IMarketChangeSuccessPayload,
  IOrdersHistorySuccessPayload,
  IPasswordChangeRequestPayload,
  IPasswordChangeSuccessPayload,
  IPasswordRecoveryRequestPayload,
  IPasswordResetRequestPayload,
  IPasswordResetSuccessPayload,
  IPaymentFieldsRequestPayload,
  IPaymentResultSuccessPayload,
  IPersonalIdentityNumberFetchRequestPayload,
  IPersonalIdentityNumberFetchSuccessPayload,
  IRegisterRequestPayload,
  IRegisterSuccessPayload,
  IRemoveVoucherSuccessPayload,
  IResponseErrorPayload,
  IResponseNotificationPayload,
  ISelectionStatePayload,
  ISetErrorPayload,
  ISetStepTitlePayload,
  IUpdateNewsletterFieldRequestPayload,
  IUpdateNewsletterFieldSuccessPayload,
  IUpdatePaymentFieldsPayload,
} from '@checkout/payloadTypes';
import { IOrder } from '@checkout/payments';
import {
  ICheckoutState,
  ICountry,
  ICustomerData,
  IFieldsData,
  ILocation,
  IPaymentFields,
  IPaymentMethod,
  IShippingMethod
} from '@checkout/state';
import * as update from 'immutability-helper';
import { createAction, handleActions } from 'redux-actions';
import ActionTypes from './../actions/actionTypes';
import {
  AccountChangeTabAction,
  AccountDetailsChangeSuccessAction,
  AddToCartSuccessAction,
  AddVoucherErrorAction,
  AddVoucherSuccessAction,
  CampaignSiteChangeSuccessAction,
  ChangeStepSuccessAction,
  CheckIngridDeliveryOptionSuccessAction,
  CountryChangeSuccessAction,
  CustomerClubFetchSuccessAction,
  DecreaseQuantitySuccessAction,
  FetchKCOWidgetSuccessAction,
  FetchSelectionErrorAction,
  FetchSelectionSuccessAction,
  LanguageChangeSuccessAction,
  LogInSuccessAction,
  LogOutSuccessAction,
  MarketChangeSuccessAction,
  OrdersHistorySuccessAction,
  PasswordChangeSuccessAction,
  PasswordRecoverySuccessAction,
  PasswordResetSuccessAction,
  PaymentFieldsSuccessAction,
  PaymentResultSuccessAction,
  PersonalIdentityNumberFetchSuccessAction,
  PreselectInitialValuesAction,
  RemoveVoucherSuccessAction,
  ResponseErrorsAction,
  ResponseNotificationsAction,
  SaveFormValuesAction,
  SetErrorAction,
  SetOrderMessageAction,
  SetStepTitleAction,
  ToggleMembersipOverlayAction,
  ToggleOrderMessageAction,
  UpdateNewsletterFieldSuccessAction,
} from './../actions/actionTypes.d';
import i18n from './../i18n';
import { ICustomerClubFetchSuccessPayload } from './../typings/payloadTypes.d';
import { preSelectCountry } from './../utils/utils';

const INITIAL_LOCALE = window.multistepCheckoutLocale || process.env.LOCALE || 'en';
const INITIAL_LOCALE_MESSAGES = (window.multistepCheckoutLocaleMessages && Object.assign(
  {},
  i18n[INITIAL_LOCALE],
  window.multistepCheckoutLocaleMessages[INITIAL_LOCALE],
)) || i18n[INITIAL_LOCALE];

export const initialState: ICheckoutState = {
  token: undefined,
  isFetching: <boolean> false,
  isLineItemFetching: <boolean> false,
  isLoaded: <boolean> false,
  selection: <ISelection> {},
  receipt: {
    order: <IOrder> {},
  },
  paymentMethods: <IPaymentMethod[]> [],
  shippingMethods: <IShippingMethod[]> [],
  paymentFields: <IPaymentFields> {},
  location: <ILocation> {},
  language: [],
  countries: <ICountry[]> [],
  widgets: <any> {},
  errors: <IError | undefined> undefined,
  notifications: <INotification | undefined> undefined,
  voucherError: undefined,
  steps: {
    current: <number> 1,
    title: '',
    isLoading: false,
  },
  pathnames: {
    checkout: '/selection',
    success: '/selection/receipt',
    failure: '/selection/failed',
    wallet: '/selection?wallet',
    account: '/account',
  },
  intlData: {
    locale: <string> INITIAL_LOCALE,
    messages: <any> INITIAL_LOCALE_MESSAGES,
  },
  ui: {
    cart: {
      isOpen: false,
    },
    membership: {
      overlay: {
        isOpen: false,
      },
      selectedTab: 'personal',
    },
  },
  fieldsData: {
    rememberMe: false,
    shippingAddress: {
      identityNumber: '',
      address1: '',
      address2: '',
      city: '',
      company: '',
      country: '',
      email: '',
      firstName: '',
      houseNumber: '',
      lastName: '',
      phoneNumber: '',
      state: '',
      zipCode: '',
    },
    billingAddress: {
      billingSameAsShipping: true,
      address1: '',
      address2: '',
      city: '',
      company: '',
      country: '',
      email: '',
      firstName: '',
      houseNumber: '',
      lastName: '',
      phoneNumber: '',
      state: '',
      zipCode: '',
    },
    contactDetails: {
      email: '',
      phoneNumber: '',
    },
    consentGroup: {
      terms: false,
    },
    deliveryForm: {
      shippingMethod: '',
    },
    shippingMethod: '',
    paymentMethod: '',
  },
  cartFieldsData: {
    additionalNotes: {
      visible: false,
      text: '',
    },
    newsletter: false,
  },
  registerFieldsData: {
    register: {
      identityNumber: '',
      firstName: '',
      lastName: '',
      email: '',
      country: '',
      address1: '',
      address2: '',
      city: '',
      physicalStore: '',
      state: '',
      phoneNumber: '',
      zipCode: '',
      newsletter: false,
      password: '',
      passwordConfirm: '',
    },
  },
  loggedIn: null,
  customerClubData: <ICustomerData> {},
  orders: null,
  ordersPaging: null,
};

const handlePreselectInitialValues = (state: ICheckoutState, action: PreselectInitialValuesAction) =>
  update(state, {
    fieldsData: {
      shippingAddress: {
        country: { $set: state.fieldsData.rememberMe ? state.fieldsData.shippingAddress.country : preSelectCountry(action.payload) },
        state: {
          $set: state.fieldsData.rememberMe ?
            state.fieldsData.shippingAddress.state : (action.payload.location.state ? action.payload.location.state : '')
        }
      },
      billingAddress: {
        country: { $set: state.fieldsData.rememberMe ? state.fieldsData.billingAddress.country : preSelectCountry(action.payload) },
        state: {
          $set: state.fieldsData.rememberMe ?
            state.fieldsData.billingAddress.state : (action.payload.location.state ? action.payload.location.state : '')
        }
      },
      shippingMethod: { $set: action.payload.selection.shippingMethod },
      paymentMethod: { $set: action.payload.selection.paymentMethod }
    },
    /* registerFieldsData: {
      register: {
        country: { $set: preSelectCountry(action.payload) }
      }
    }, */
  });

const handleFetchSelectionSuccess = (state: ICheckoutState, action: FetchSelectionSuccessAction) =>
  update(state, {
    isFetching: { $set: false },
    token: { $set: action.payload.token },
    selection: { $set: action.payload.selection },
    loggedIn: { $set: !(action.payload.loggedIn) || action.payload.loggedIn === undefined ? null : action.payload.loggedIn },
    paymentMethods: { $set: action.payload.paymentMethods },
    shippingMethods: { $set: action.payload.shippingMethods },
    paymentFields: { $set: action.payload.paymentFields },
    location: { $set: action.payload.location },
    language: { $set: action.payload.language },
    countries: { $set: action.payload.countries },
    fieldsData: {
      shippingMethod: { $set: action.payload.selection && action.payload.selection.shippingMethod },
      paymentMethod: { $set: action.payload.selection && action.payload.selection.paymentMethod }
    }
  });

const handleMarketContextChangeSuccess = (state: ICheckoutState, action: MarketChangeSuccessAction) =>
  update(state, {
    isFetching: { $set: false },
    token: { $set: action.payload.token },
    selection: { $set: action.payload.selection },
    paymentMethods: { $set: action.payload.paymentMethods },
    shippingMethods: { $set: action.payload.shippingMethods },
    paymentFields: { $set: action.payload.paymentFields },
    location: { $set: action.payload.location },
    language: { $set: action.payload.language },
    countries: { $set: action.payload.countries },
    fieldsData: {
      shippingAddress: {
        country: { $set: action.payload.location.country },
        state: { $set: action.payload.location.state ? action.payload.location.state : '' }
      },
      shippingMethod: { $set: action.payload.selection && action.payload.selection.shippingMethod },
      paymentMethod: { $set: action.payload.selection && action.payload.selection.paymentMethod }
    }
  });

const handleCountryChangeSuccess = (state: ICheckoutState, action: CountryChangeSuccessAction) =>
  update(state, {
    isFetching: { $set: false },
    selection: { $set: action.payload.selection },
    paymentMethods: { $set: action.payload.paymentMethods },
    shippingMethods: { $set: action.payload.shippingMethods },
    paymentFields: { $set: action.payload.paymentFields },
    location: { $set: action.payload.location },
    language: { $set: action.payload.language },
    countries: { $set: action.payload.countries },
    fieldsData: {
      [action.payload.formName]: {
        country: { $set: action.payload.location.country },
        state: { $set: action.payload.location.state ? action.payload.location.state : '' }
      },
      shippingMethod: { $set: action.payload.selection.shippingMethod },
      paymentMethod: { $set: action.payload.selection.paymentMethod }
    }
  });

const handleFetchSelectionError = (state: ICheckoutState, action: FetchSelectionErrorAction) =>
  update(state, {
    isFetching: { $set: false },
    // errors: { details: { $set: payload } },
  });

const handleAsyncRequest = (state: ICheckoutState) =>
  update(state, {
    isFetching: { $set: true },
  });

const handleAsyncLineRequest = (state: ICheckoutState) =>
  update(state, {
    isFetching: { $set: true },
    isLineItemFetching: { $set: true },
  });

const handleAddToCartSuccess = (state: ICheckoutState, action: AddToCartSuccessAction) =>
  update(state, {
    isFetching: { $set: false },
    selection: { $set: action.payload.selection },
    paymentMethods: { $set: action.payload.paymentMethods },
    shippingMethods: { $set: action.payload.shippingMethods },
    paymentFields: { $set: action.payload.paymentFields },
    location: { $set: action.payload.location },
    countries: { $set: action.payload.countries },
    token: { $set: action.payload.token },
  });

const handleLineItemAddRemoveSuccess = (state: ICheckoutState, action: DecreaseQuantitySuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    isLineItemFetching: { $set: false },
    selection: {
      totals: { $set: action.payload.selection.totals },
      items: { $set: action.payload.selection.items },
      discounts: { $set: action.payload.selection.discounts },
      shippingMethod: { $set: action.payload.selection.shippingMethod },
      shippingMethodName: { $set: action.payload.selection.shippingMethodName },
    },
    shippingMethods: { $set: action.payload.shippingMethods },
    token: { $set: action.payload.token },
  });

const handlePersistRehydrate = (state: ICheckoutState, { payload }: any) => {
  const incoming = payload && payload.checkout;
  return { ...state, ...incoming, isLoaded: true };
};

const handleChangeStepSuccess = (state: ICheckoutState, action: ChangeStepSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    steps: {
      current: { $set: action.payload.step }
    },
  });

const handleSetStepTitle = (state: ICheckoutState, action: SetStepTitleAction): ICheckoutState =>
  update(state, {
    steps: {
      title: { $set: action.payload.title }
    },
  });

const handleFetchWidgetSuccess = (state: ICheckoutState, action: FetchKCOWidgetSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    widgets: {
      $merge: {
        [action.payload.selectedPaymentMethod]: action.payload.formHtml,
      }
    }
  });

const handleFetchKCOWidgetCancel = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
  });

const handleCheckoutSuccess = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
  });

const handleSetError = (state: ICheckoutState, action: SetErrorAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    isLineItemFetching: { $set: false },
    errors: { $set: action.payload },
  });

const handleShipwalletStepRequest = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    steps: {
      isLoading: { $set: true },
    }
  });

const handleShipwalletStepSuccess = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    steps: {
      isLoading: { $set: !state.steps.isLoading },
    }
  });

const handleCountryChangeCancel = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
  });

const handlePaymentResultSuccess = (state: ICheckoutState, action: PaymentResultSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    token: { $set: action.payload.loggedIn ? action.payload.token : undefined },
    steps: {
      current: { $set: 1 }
    },
    selection: { $set: {} },
    widgets: { $set: {} },
    receipt: {
      order: { $set: action.payload.order },
    },
    fieldsData: {
      shippingAddress: { $set: state.fieldsData.rememberMe || state.loggedIn ? state.fieldsData.shippingAddress : initialState.fieldsData.shippingAddress },
      billingAddress: { $set: state.fieldsData.rememberMe ? state.fieldsData.billingAddress : initialState.fieldsData.billingAddress },
      contactDetails: { $set: state.fieldsData.rememberMe || state.loggedIn ? state.fieldsData.contactDetails : initialState.fieldsData.contactDetails },
      consentGroup: { $set: initialState.fieldsData.consentGroup },
    },
    cartFieldsData: {
      additionalNotes: { $set: initialState.cartFieldsData.additionalNotes },
      newsletter: { $set: initialState.cartFieldsData.newsletter }
    },
  });

const handleResponseErrors = (state: ICheckoutState, action: ResponseErrorsAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    errors: { $set: action.payload.errors },
    selection: {
      totals: { $set: action.payload.selection && action.payload.selection.totals },
      items: { $set: action.payload.selection && action.payload.selection.items },
    },
    unavailable: { $set: action.payload.unavailable },
  });

const handleResetErrors = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    errors: { $set: undefined },
  });

const handleResponseNotifications = (state: ICheckoutState, action: ResponseNotificationsAction): ICheckoutState =>
  update(state, {
    notifications: { $set: action.payload },
  });

const handleResetNotifications = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    notifications: { $set: undefined },
  });

const handleDeliveryOptionCheckSuccessSuccess = (state: ICheckoutState, action: CheckIngridDeliveryOptionSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    selection: { $set: action.payload.selection },
    paymentMethods: { $set: action.payload.paymentMethods },
    shippingMethods: { $set: action.payload.shippingMethods },
    paymentFields: { $set: action.payload.paymentFields },
    fieldsData: {
      /* shippingAddress: {
        address1: { $set: action.payload.selection.shippingAddress.address1 },
        address2: { $set: action.payload.selection.shippingAddress.address2 },
        city: { $set: action.payload.selection.shippingAddress.city },
        country: { $set: action.payload.selection.shippingAddress.country },
        zipCode: { $set: action.payload.selection.shippingAddress.zipCode },
      }, */
    },
  });

const handleUpdatePaymentFieldsSuccess = (state: ICheckoutState, action: PaymentFieldsSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    selection: { $set: action.payload.selection },
    paymentMethods: { $set: action.payload.paymentMethods },
    shippingMethods: { $set: action.payload.shippingMethods },
    paymentFields: { $set: action.payload.paymentFields },
  });

const handleUpdateNewsletterFieldSuccess = (state: ICheckoutState, action: UpdateNewsletterFieldSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    cartFieldsData: {
      newsletter: { $set: action.payload.newsletter },
    }
  });

const handleToggleCartVisibility = (state: ICheckoutState): ICheckoutState => {
  return update(state, {
    ui: {
      cart: {
        isOpen: { $set: !state.ui.cart.isOpen }
      }
    },
  });
};

const handleCloseCart = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    ui: {
      cart: {
        isOpen: { $set: false }
      }
    },
  });

const handleOpenCart = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    ui: {
      cart: {
        isOpen: { $set: true }
      }
    },
  });

const handleAddVoucherSuccess = (state: ICheckoutState, action: AddVoucherSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    selection: { $set: action.payload.selection },
    shippingMethods: { $set: action.payload.shippingMethods },
  });

const handleAddVoucherError = (state: ICheckoutState, action: AddVoucherErrorAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    voucherError: { $set: action.payload },
  });

const handleRemoveVoucherSuccess = (state: ICheckoutState, action: RemoveVoucherSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    selection: { $set: action.payload.selection },
    shippingMethods: { $set: action.payload.shippingMethods },
  });

const handleChangeLanguageSuccess = (state: ICheckoutState, action: LanguageChangeSuccessAction): ICheckoutState => {
  const intlData = ('payload' in action)
    ? {
      locale: action.payload.location.language,
      messages: (window.multistepCheckoutLocaleMessages && Object.assign(
        {},
        i18n[action.payload.location.language],
        window.multistepCheckoutLocaleMessages[action.payload.location.language],
      )) || i18n[action.payload.location.language]
    }
    : initialState.intlData;

  return update(state, {
    isFetching: { $set: false },
    intlData: { $set: intlData },
    selection: { $set: action.payload.selection },
    paymentMethods: { $set: action.payload.paymentMethods },
    shippingMethods: { $set: action.payload.shippingMethods },
    paymentFields: { $set: action.payload.paymentFields },
    location: { $set: action.payload.location },
    language: { $set: action.payload.language },
  });
};

const handleUpdateIntlData = (state: ICheckoutState): ICheckoutState => {
  const intlData = (state.intlData.locale)
    ? {
      locale: (window.multistepCheckoutLocale && window.multistepCheckoutLocale) || state.intlData.locale,
      messages: (window.multistepCheckoutLocaleMessages && Object.assign(
        {},
        i18n[state.intlData.locale],
        window.multistepCheckoutLocaleMessages[state.intlData.locale],
      )) || i18n[state.intlData.locale]
    }
    : initialState.intlData;

  return update(state, {
    intlData: { $set: intlData },
  });
};

const handleClearVoucherError = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    voucherError: { $set: '' },
  });

const handleSaveFormValues = (state: ICheckoutState, action: SaveFormValuesAction): ICheckoutState =>
  update(state, {
    fieldsData: { $set: action.payload.fieldsData }
  });

const handleToggleOrderMessage = (state: ICheckoutState, action: ToggleOrderMessageAction): ICheckoutState =>
  update(state, {
    cartFieldsData: {
      additionalNotes: {
        visible: { $set: action.payload.isActive }
      }
    }
  });

const handleSetOrderMessage = (state: ICheckoutState, action: SetOrderMessageAction): ICheckoutState =>
  update(state, {
    cartFieldsData: {
      additionalNotes: {
        text: { $set: action.payload.value }
      }
    }
  });

const handleToggleMembershipOverlay = (state: ICheckoutState, action: ToggleMembersipOverlayAction): ICheckoutState =>
  update(state, {
    errors: { $set: {} },
    ui: {
      membership: {
        overlay: {
          isOpen: { $set: action.payload.isOpen },
        },
      }
    }
  });

const handleLoginSuccess = (state: ICheckoutState, action: LogInSuccessAction): ICheckoutState => {
  return update(state, {
    isFetching: { $set: false },
    selection: { $set: action.payload.selection },
    paymentMethods: { $set: action.payload.paymentMethods },
    shippingMethods: { $set: action.payload.shippingMethods },
    paymentFields: { $set: action.payload.paymentFields },
    location: { $set: action.payload.location },
    language: { $set: action.payload.language },
    countries: { $set: action.payload.countries },
    loggedIn: { $set: action.payload.loggedIn },
    fieldsData: {
      shippingAddress: {
        address1: { $set: action.payload.loggedIn && action.payload.loggedIn.address1 },
        address2: { $set: action.payload.loggedIn && action.payload.loggedIn.address2 },
        city: { $set: action.payload.loggedIn && action.payload.loggedIn.city },
        country: { $set: action.payload.loggedIn && action.payload.loggedIn.country ? action.payload.loggedIn.country : action.payload.location.country },
        state: {
          $set: action.payload.loggedIn && action.payload.loggedIn.state
            ? action.payload.loggedIn.state :
            (action.payload.location.state ? action.payload.location.state : '')
        },
        firstName: { $set: action.payload.loggedIn && action.payload.loggedIn.firstName },
        lastName: { $set: action.payload.loggedIn && action.payload.loggedIn.lastName },
        zipCode: { $set: action.payload.loggedIn && action.payload.loggedIn.zipCode },
        newsletter: { $set: action.payload.loggedIn && action.payload.loggedIn.newsletter },
      },
      contactDetails: {
        phoneNumber: { $set: action.payload.loggedIn && action.payload.loggedIn.phoneNumber },
        email: { $set: action.payload.loggedIn && action.payload.loggedIn.email },
      },
    },
    token: { $set: action.payload.token },
  });
};

const handleRegisterSuccess = (state: ICheckoutState, action: LogInSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    selection: { $set: action.payload.selection },
    paymentMethods: { $set: action.payload.paymentMethods },
    shippingMethods: { $set: action.payload.shippingMethods },
    paymentFields: { $set: action.payload.paymentFields },
    location: { $set: action.payload.location },
    language: { $set: action.payload.language },
    countries: { $set: action.payload.countries },
    loggedIn: { $set: action.payload.loggedIn },
    fieldsData: {
      shippingAddress: {
        address1: { $set: action.payload.loggedIn && action.payload.loggedIn.address1 },
        address2: { $set: action.payload.loggedIn && action.payload.loggedIn.address2 },
        city: { $set: action.payload.loggedIn && action.payload.loggedIn.city },
        country: { $set: action.payload.loggedIn && action.payload.loggedIn.country ? action.payload.loggedIn.country : action.payload.location.country },
        state: {
          $set: action.payload.loggedIn && action.payload.loggedIn.state
            ? action.payload.loggedIn.state :
            (action.payload.location.state ? action.payload.location.state : '')
        },
        firstName: { $set: action.payload.loggedIn && action.payload.loggedIn.firstName },
        lastName: { $set: action.payload.loggedIn && action.payload.loggedIn.lastName },
        zipCode: { $set: action.payload.loggedIn && action.payload.loggedIn.zipCode },
      },
      contactDetails: {
        phoneNumber: { $set: action.payload.loggedIn && action.payload.loggedIn.phoneNumber },
        email: { $set: action.payload.loggedIn && action.payload.loggedIn.email },
      },
    },
    token: { $set: action.payload.token },
  });

const handlePasswordRecoverySuccess = (state: ICheckoutState, action: PasswordRecoverySuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    notifications: { $set: action.payload }
  });

const handlePasswordResetSuccess = (state: ICheckoutState, action: PasswordResetSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    selection: { $set: action.payload.selection },
    loggedIn: { $set: action.payload.loggedIn },
    token: { $set: action.payload.token },
  });

const handleLogOutSuccess = (state: ICheckoutState, action: LogOutSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    selection: { $set: action.payload.selection },
    fieldsData: { $set: initialState.fieldsData },
    cartFieldsData: { $set: initialState.cartFieldsData },
    loggedIn: { $set: null },
    customerClubData: { $set: {} },
    token: { $set: undefined },
  });

const handlePasswordChangeSuccess = (state: ICheckoutState, action: PasswordChangeSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
  });

const handleAccountDetailsChangeSuccess = (state: ICheckoutState, action: AccountDetailsChangeSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    loggedIn: { $set: action.payload.loggedIn },
    fieldsData: {
      shippingAddress: {
        address1: { $set: action.payload.loggedIn && action.payload.loggedIn.address1 },
        address2: { $set: action.payload.loggedIn && action.payload.loggedIn.address2 },
        city: { $set: action.payload.loggedIn && action.payload.loggedIn.city },
        country: { $set: action.payload.loggedIn && action.payload.loggedIn.country },
        state: {
          $set: action.payload.loggedIn && action.payload.loggedIn.state
            ? action.payload.loggedIn.state :
            (action.payload.location.state ? action.payload.location.state : '')
        },
        firstName: { $set: action.payload.loggedIn && action.payload.loggedIn.firstName },
        lastName: { $set: action.payload.loggedIn && action.payload.loggedIn.lastName },
        zipCode: { $set: action.payload.loggedIn && action.payload.loggedIn.zipCode },
      },
      contactDetails: {
        phoneNumber: { $set: action.payload.loggedIn && action.payload.loggedIn.phoneNumber },
        email: { $set: action.payload.loggedIn && action.payload.loggedIn.email },
      },
    },
  });

const handleAccountChangeTab = (state: ICheckoutState, action: AccountChangeTabAction): ICheckoutState =>
  update(state, {
    ui: {
      membership: {
        selectedTab: { $set: action.payload.type },
      }
    },
  });

const handlePersonalIdentityNumberFetchSuccess = (state: ICheckoutState, action: PersonalIdentityNumberFetchSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    fieldsData: {
      shippingAddress: {
        firstName: { $set: action.payload.address.firstName },
        lastName: { $set: action.payload.address.lastName },
        address1: { $set: action.payload.address.address1 },
        zipCode: { $set: action.payload.address.zipCode },
        city: { $set: action.payload.address.city },
        country: { $set: action.payload.address.country },
      }
    }
  });

const handleOrdersHistoryRequest = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    isFetching: { $set: true },
  });

const handleOrdersHistorySuccess = (state: ICheckoutState, action: OrdersHistorySuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    orders: { $set: action.payload.orders },
    ordersPaging: { $set: action.payload.ordersPaging },
  });

const handleCustomerFetchRequest = (state: ICheckoutState): ICheckoutState =>
  update(state, {
    isFetching: { $set: true },
  });

const handleCustomerFetchSuccess = (state: ICheckoutState, action: CustomerClubFetchSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    customerClubData: { $set: action.payload },
  });

const handleCampaignSiteChangeSuccess = (state: ICheckoutState, action: CampaignSiteChangeSuccessAction): ICheckoutState =>
  update(state, {
    isFetching: { $set: false },
    selection: { $set: action.payload.selection },
    token: { $set: action.payload.token },
  });

export default handleActions<ICheckoutState>({
  [ActionTypes.PRESELECT_INITIAL_VALUES]: handlePreselectInitialValues,

  [ActionTypes.FETCH_SELECTION_REQUEST]: handleAsyncRequest,
  [ActionTypes.FETCH_SELECTION_SUCCESS]: handleFetchSelectionSuccess,
  [ActionTypes.FETCH_SELECTION_ERROR]: handleFetchSelectionError,

  // Add to cart
  [ActionTypes.ITEM_ADD_REQUEST]: handleAsyncRequest,
  [ActionTypes.ITEM_ADD_SUCCESS]: handleAddToCartSuccess,

  // Quantity change actions
  [ActionTypes.LINE_ITEM_ADD_REQUEST]: handleAsyncLineRequest,
  [ActionTypes.LINE_ITEM_ADD_SUCCESS]: handleLineItemAddRemoveSuccess,
  [ActionTypes.LINE_ITEM_REMOVE_REQUEST]: handleAsyncLineRequest,
  [ActionTypes.LINE_ITEM_REMOVE_SUCCESS]: handleLineItemAddRemoveSuccess,

  [ActionTypes.REHYDRATE]: handlePersistRehydrate,

  [ActionTypes.CHANGE_STEP_REQUEST]: handleAsyncRequest,
  [ActionTypes.CHANGE_STEP_SUCCESS]: handleChangeStepSuccess,
  [ActionTypes.SET_STEP_TITLE]: handleSetStepTitle,

  // Widgets
  [ActionTypes.FETCH_KCO_WIDGET_REQUEST]: handleAsyncRequest,
  [ActionTypes.FETCH_KCO_WIDGET_SUCCESS]: handleFetchWidgetSuccess,
  [ActionTypes.FETCH_KCO_WIDGET_CANCEL]: handleFetchKCOWidgetCancel,

  [ActionTypes.FETCH_WIDGET_SUCCESS]: handleFetchWidgetSuccess,

  [ActionTypes.HANDLE_SHIPWALLET_STEP_REQUEST]: handleShipwalletStepRequest,
  [ActionTypes.HANDLE_SHIPWALLET_STEP_SUCCESS]: handleShipwalletStepSuccess,

  // Country
  [ActionTypes.COUNTRY_CHANGE_REQUEST]: handleAsyncRequest,
  [ActionTypes.COUNTRY_CHANGE_SUCCESS]: handleCountryChangeSuccess,
  [ActionTypes.COUNTRY_CHANGE_CANCEL]: handleCountryChangeCancel,

  // State
  [ActionTypes.COUNTRY_STATE_CHANGE_REQUEST]: handleAsyncRequest,
  [ActionTypes.COUNTRY_STATE_CHANGE_SUCCESS]: handleCountryChangeSuccess,

  // Payment
  [ActionTypes.PAYMENT_RESULT_REQUEST]: handleAsyncRequest,
  [ActionTypes.PAYMENT_RESULT_SUCCESS]: handlePaymentResultSuccess,

  // Errors
  [ActionTypes.SET_ERRORS]: handleSetError,
  [ActionTypes.RESPONSE_ERRORS]: handleResponseErrors,
  [ActionTypes.RESET_ERRORS]: handleResetErrors,

  // Messages
  [ActionTypes.RESPONSE_NOTIFICATIONS]: handleResponseNotifications,
  [ActionTypes.RESET_NOTIFICATIONS]: handleResetNotifications,

  // Payment fields
  [ActionTypes.UPDATE_PAYMENT_FIELDS_REQUEST]: handleAsyncRequest,
  [ActionTypes.UPDATE_PAYMENT_FIELDS_SUCCESS]: handleUpdatePaymentFieldsSuccess,

  // Payment fields
  [ActionTypes.UPDATE_NEWSLETTER_FIELD_REQUEST]: handleAsyncRequest,
  [ActionTypes.UPDATE_NEWSLETTER_FIELD_SUCCESS]: handleUpdateNewsletterFieldSuccess,

  // Ingrid fields
  [ActionTypes.INGRID_DELIVERY_OPTION_CHECK_REQUEST]: handleAsyncRequest,
  [ActionTypes.INGRID_DELIVERY_OPTION_CHECK_SUCCESS]: handleDeliveryOptionCheckSuccessSuccess,

  // Cart
  [ActionTypes.TOGGLE_CART_VISIBILITY]: handleToggleCartVisibility,
  [ActionTypes.OPEN_CART]: handleOpenCart,
  [ActionTypes.CLOSE_CART]: handleCloseCart,

  // Promo code
  [ActionTypes.ADD_VOUCHER_REQUEST]: handleAsyncRequest,
  [ActionTypes.ADD_VOUCHER_SUCCESS]: handleAddVoucherSuccess,
  [ActionTypes.ADD_VOUCHER_ERROR]: handleAddVoucherError,
  [ActionTypes.REMOVE_VOUCHER_REQUEST]: handleAsyncRequest,
  [ActionTypes.REMOVE_VOUCHER_SUCCESS]: handleRemoveVoucherSuccess,
  [ActionTypes.CLEAR_VOUCHER_ERROR]: handleClearVoucherError,

  // Languages
  [ActionTypes.LOCALE_TRANSLATIONS_FETCH_REQUEST]: handleAsyncRequest,
  [ActionTypes.LOCALE_TRANSLATIONS_FETCH_SUCCESS]: handleChangeLanguageSuccess,
  [ActionTypes.UPDATE_INTL_DATA]: handleUpdateIntlData,

  // Market change
  [ActionTypes.CHANGE_MARKET_CONTEXT_REQUEST]: handleAsyncRequest,
  [ActionTypes.CHANGE_MARKET_CONTEXT_SUCCESS]: handleMarketContextChangeSuccess,

  // Shipping method change
  [ActionTypes.CHANGE_SHIPPING_METHOD_REQUEST]: handleAsyncRequest,
  [ActionTypes.CHANGE_SHIPPING_METHOD_SUCCESS]: handleFetchSelectionSuccess,

  // Payment method change
  [ActionTypes.CHANGE_PAYMENT_METHOD_REQUEST]: handleAsyncRequest,
  [ActionTypes.CHANGE_PAYMENT_METHOD_SUCCESS]: handleFetchSelectionSuccess,

  // Payment method change
  [ActionTypes.CHECKOUT_REQUEST]: handleAsyncRequest,
  [ActionTypes.CHECKOUT_SUCCESS]: handleCheckoutSuccess,
  [ActionTypes.SAVE_FORM_VALUES]: handleSaveFormValues,
  [ActionTypes.TOGGLE_ORDER_MESSAGE_VISIBILITY]: handleToggleOrderMessage,
  [ActionTypes.SET_ORDER_MESSAGE_TEXT]: handleSetOrderMessage,

  // Membership
  [ActionTypes.TOGGLE_MEMBERSHIP_OVERLAY]: handleToggleMembershipOverlay,
  [ActionTypes.LOG_IN_REQUEST]: handleAsyncRequest,
  [ActionTypes.LOG_IN_SUCCESS]: handleLoginSuccess,
  [ActionTypes.REGISTER_REQUEST]: handleAsyncRequest,
  [ActionTypes.REGISTER_SUCCESS]: handleRegisterSuccess,
  [ActionTypes.PASSWORD_RECOVERY_REQUEST]: handleAsyncRequest,
  [ActionTypes.PASSWORD_RECOVERY_SUCCESS]: handlePasswordRecoverySuccess,
  [ActionTypes.PASSWORD_RESET_REQUEST]: handleAsyncRequest,
  [ActionTypes.PASSWORD_RESET_SUCCESS]: handlePasswordResetSuccess,
  [ActionTypes.LOG_OUT_REQUEST]: handleAsyncRequest,
  [ActionTypes.LOG_OUT_SUCCESS]: handleLogOutSuccess,
  [ActionTypes.PASSWORD_CHANGE_REQUEST]: handleAsyncRequest,
  [ActionTypes.PASSWORD_CHANGE_SUCCESS]: handlePasswordChangeSuccess,
  [ActionTypes.ACCOUNT_DETAILS_CHANGE_REQUEST]: handleAsyncRequest,
  [ActionTypes.ACCOUNT_DETAILS_CHANGE_SUCCESS]: handleAccountDetailsChangeSuccess,
  [ActionTypes.ACCOUNT_CHANEGE_TAB]: handleAccountChangeTab,
  [ActionTypes.ORDERS_HISTORY_REQUEST]: handleOrdersHistoryRequest,
  [ActionTypes.ORDERS_HISTORY_SUCCESS]: handleOrdersHistorySuccess,
  [ActionTypes.CUSTOMER_CLUB_FETCH_REQUEST]: handleCustomerFetchRequest,
  [ActionTypes.CUSTOMER_CLUB_FETCH_SUCCESS]: handleCustomerFetchSuccess,

  // Personal Identity Number
  [ActionTypes.PERSONAL_IDENTITY_NUMBER_FETCH_REQUEST]: handleAsyncRequest,
  [ActionTypes.PERSONAL_IDENTITY_NUMBER_FETCH_SUCCESS]: handlePersonalIdentityNumberFetchSuccess,

  // Campaign
  [ActionTypes.CAMPAIGN_SITE_CHANGE_REQUEST]: handleAsyncRequest,
  [ActionTypes.CAMPAIGN_SITE_CHANGE_SUCCESS]: handleCampaignSiteChangeSuccess,
}, initialState);

// Preselect initial values from API
export const preselectInitialValues = createAction(ActionTypes.PRESELECT_INITIAL_VALUES);

// Fetch selection
export const fetchSelectionRequest = createAction(ActionTypes.FETCH_SELECTION_REQUEST);
export const fetchSelectionSuccess = createAction<ISelectionStatePayload>(ActionTypes.FETCH_SELECTION_SUCCESS);
export const fetchSelectionError = createAction<ISetErrorPayload>(ActionTypes.FETCH_SELECTION_ERROR);

// Add to cart
export const addToCart = createAction<IAddToCartRequestPayload>(ActionTypes.ITEM_ADD_REQUEST);
export const addToCartSuccess = createAction<IAddToCartSuccessPayload>(ActionTypes.ITEM_ADD_SUCCESS);

// Increase quantity
export const addLineItemRequest = createAction<ILineItemAddRequestPayload>(ActionTypes.LINE_ITEM_ADD_REQUEST);
export const addLineItemSuccess = createAction<ILineItemAddSuccessPayload>(ActionTypes.LINE_ITEM_ADD_SUCCESS);
export const addLineItemError = createAction<ISetErrorPayload>(ActionTypes.LINE_ITEM_ADD_ERROR);

// Decrease quantity
export const removeLineItemRequest = createAction<ILineItemRemoveRequestPayload>(ActionTypes.LINE_ITEM_REMOVE_REQUEST);
export const removeLineItemSuccess = createAction<ILineItemRemoveSuccessPayload>(ActionTypes.LINE_ITEM_REMOVE_SUCCESS);
export const removeLineItemError = createAction<ISetErrorPayload>(ActionTypes.LINE_ITEM_REMOVE_ERROR);

// Steps
export const changeStepRequest = createAction<IChangeStepPayload>(ActionTypes.CHANGE_STEP_REQUEST);
export const changeStepSuccess = createAction<IChangeStepPayload>(ActionTypes.CHANGE_STEP_SUCCESS);
export const setStepTitle = createAction<ISetStepTitlePayload>(ActionTypes.SET_STEP_TITLE);

// Fetch KCO
export const fetchKCOWidgetRequest = createAction(ActionTypes.FETCH_KCO_WIDGET_REQUEST);
export const fetchKCOWidgetSuccess = createAction<IFetchWidgetSuccessPayload>(ActionTypes.FETCH_KCO_WIDGET_SUCCESS);
export const fetchKCOWidgetCancel = createAction(ActionTypes.FETCH_KCO_WIDGET_CANCEL);

export const fetchWidgetSuccess = createAction<IFetchWidgetSuccessPayload>(ActionTypes.FETCH_WIDGET_SUCCESS);

// Shipwallet
export const shipwalletStepRequest = createAction(ActionTypes.HANDLE_SHIPWALLET_STEP_REQUEST);
export const shipwalletStepSuccess = createAction(ActionTypes.HANDLE_SHIPWALLET_STEP_SUCCESS);

// Country
export const countryChangeRequest = createAction<ICountryChangeRequestPayload>(ActionTypes.COUNTRY_CHANGE_REQUEST);
export const countryChangeSuccess = createAction<ICountryChangeSuccessPayload>(ActionTypes.COUNTRY_CHANGE_SUCCESS);
export const countryChangeCancel = createAction(ActionTypes.COUNTRY_CHANGE_CANCEL);

// State
export const countryStateChangeRequest = createAction<ICountryStateChangePayload>(ActionTypes.COUNTRY_STATE_CHANGE_REQUEST);
export const countryStateChangeSuccess = createAction<ICountryChangeSuccessPayload>(ActionTypes.COUNTRY_STATE_CHANGE_SUCCESS);

// Languages
export const languageChangeRequest = createAction<ILanguageChangeRequestPayload>(ActionTypes.LOCALE_TRANSLATIONS_FETCH_REQUEST);
export const languageChangeSuccess = createAction<ILanguageChangeSuccessPayload>(ActionTypes.LOCALE_TRANSLATIONS_FETCH_SUCCESS);
export const languageFallback = createAction<string>(ActionTypes.LOCALE_TRANSLATIONS_FETCH_FALLBACK);
export const updateIntlData = createAction(ActionTypes.UPDATE_INTL_DATA);

// Market change
export const marketChangeRequest = createAction<IMarketChangeRequestPayload>(ActionTypes.CHANGE_MARKET_CONTEXT_REQUEST);
export const marketChangeSuccess = createAction<IMarketChangeSuccessPayload>(ActionTypes.CHANGE_MARKET_CONTEXT_SUCCESS);

// Shipping method change
export const shippingMethodChangeRequest = createAction<IChangeShippingMethodRequestPayload>(ActionTypes.CHANGE_SHIPPING_METHOD_REQUEST);
export const shippingMethodChangeSuccess = createAction<IChangeShippingMethodSuccessPayload>(ActionTypes.CHANGE_SHIPPING_METHOD_SUCCESS);

// Payment method change
export const paymentMethodChangeRequest = createAction<IChangePaymentMethodRequestPayload>(ActionTypes.CHANGE_PAYMENT_METHOD_REQUEST);
export const paymentMethodChangeSuccess = createAction<IChangePaymentMethodSuccessPayload>(ActionTypes.CHANGE_SHIPPING_METHOD_SUCCESS);

// Receipt
export const paymentResultRequest = createAction(ActionTypes.PAYMENT_RESULT_REQUEST);
export const paymentResultSuccess = createAction<IPaymentResultSuccessPayload>(ActionTypes.PAYMENT_RESULT_SUCCESS);

// Failure
export const paymentResultFailure = createAction(ActionTypes.PAYMENT_RESULT_FAILURE);

// Payment fields
export const updatePaymentFieldsRequest = createAction<IPaymentFieldsRequestPayload>(ActionTypes.UPDATE_PAYMENT_FIELDS_REQUEST);
export const updatePaymentFieldsSuccess = createAction<IUpdatePaymentFieldsPayload>(ActionTypes.UPDATE_PAYMENT_FIELDS_SUCCESS);

// Newsletter field
export const updateNewsletterFieldRequest = createAction<IUpdateNewsletterFieldRequestPayload>(ActionTypes.UPDATE_NEWSLETTER_FIELD_REQUEST);
export const updateNewsletterFieldSuccess = createAction<IUpdateNewsletterFieldSuccessPayload>(ActionTypes.UPDATE_NEWSLETTER_FIELD_SUCCESS);

// Ingrid fields
export const checkIngridDeliveryOptionRequest = createAction<IPaymentFieldsRequestPayload>(ActionTypes.INGRID_DELIVERY_OPTION_CHECK_REQUEST);
export const checkIngridDeliveryOptionSuccess = createAction<IUpdatePaymentFieldsPayload>(ActionTypes.INGRID_DELIVERY_OPTION_CHECK_SUCCESS);

// Errors
export const setErrors = createAction<ISetErrorPayload>(ActionTypes.SET_ERRORS);
export const responseError = createAction<IResponseErrorPayload>(ActionTypes.RESPONSE_ERRORS);
export const resetErrors = createAction(ActionTypes.RESET_ERRORS);

// Messages
export const responseNotifications = createAction<IResponseNotificationPayload>(ActionTypes.RESPONSE_NOTIFICATIONS);
export const resetNotifications = createAction(ActionTypes.RESET_NOTIFICATIONS);

// Cart
export const toggleCartVisibility = createAction(ActionTypes.TOGGLE_CART_VISIBILITY);
export const openCart = createAction(ActionTypes.OPEN_CART);
export const closeCart = createAction(ActionTypes.CLOSE_CART);

// Promo input (voucher code)
export const addVoucherRequest = createAction<string>(ActionTypes.ADD_VOUCHER_REQUEST);
export const addVoucherSuccess = createAction<IAddVoucherSuccessPayload>(ActionTypes.ADD_VOUCHER_SUCCESS);
export const addVoucherError = createAction<string>(ActionTypes.ADD_VOUCHER_ERROR);
export const removeVoucherRequest = createAction<string>(ActionTypes.REMOVE_VOUCHER_REQUEST);
export const removeVoucherSuccess = createAction<IRemoveVoucherSuccessPayload>(ActionTypes.REMOVE_VOUCHER_SUCCESS);
export const clearVoucherError = createAction(ActionTypes.CLEAR_VOUCHER_ERROR);

// Handle checkout (payment)
export const checkoutRequest = createAction(ActionTypes.CHECKOUT_REQUEST);
export const checkoutSuccess = createAction(ActionTypes.CHECKOUT_SUCCESS);

// Forms (formik) fields
export const saveFormValues = createAction<{ fieldsData: IFieldsData; }>(ActionTypes.SAVE_FORM_VALUES);

// Order message
export const toggleOrderMessage = createAction<{ isActive: boolean; }>(ActionTypes.TOGGLE_ORDER_MESSAGE_VISIBILITY);
export const setOrderMessageText = createAction<{ value: string; }>(ActionTypes.SET_ORDER_MESSAGE_TEXT);

// Membership
export const toggleMembershipOverlay = createAction<{ isOpen: boolean; }>(ActionTypes.TOGGLE_MEMBERSHIP_OVERLAY);
export const logInRequest = createAction<ILogInRequestPayload>(ActionTypes.LOG_IN_REQUEST);
export const logInSuccess = createAction<ILogInSuccessPayload>(ActionTypes.LOG_IN_SUCCESS);
export const registerRequest = createAction<IRegisterRequestPayload>(ActionTypes.REGISTER_REQUEST);
export const registerSuccess = createAction<IRegisterSuccessPayload>(ActionTypes.REGISTER_SUCCESS);
export const passwordRecoveryRequest = createAction<IPasswordRecoveryRequestPayload>(ActionTypes.PASSWORD_RECOVERY_REQUEST);
export const passwordRecoverySuccess = createAction(ActionTypes.PASSWORD_RECOVERY_SUCCESS);
export const passwordResetRequest = createAction<IPasswordResetRequestPayload>(ActionTypes.PASSWORD_RESET_REQUEST);
export const passwordResetSuccess = createAction<IPasswordResetSuccessPayload>(ActionTypes.PASSWORD_RESET_SUCCESS);
export const logOutRequest = createAction(ActionTypes.LOG_OUT_REQUEST);
export const logOutSuccess = createAction<ILogOutSuccessPayload>(ActionTypes.LOG_OUT_SUCCESS);
export const passwordChangeRequest = createAction<IPasswordChangeRequestPayload>(ActionTypes.PASSWORD_CHANGE_REQUEST);
export const passwordChangeSuccess = createAction<IPasswordChangeSuccessPayload>(ActionTypes.PASSWORD_CHANGE_SUCCESS);
export const accountDetailsChangeRequest = createAction<IAccountDetailsChangeRequestPayload>(ActionTypes.ACCOUNT_DETAILS_CHANGE_REQUEST);
export const accountDetailsChangeSuccess = createAction<IAccountDetailsChangeSuccessPayload>(ActionTypes.ACCOUNT_DETAILS_CHANGE_SUCCESS);
export const accountChangeTab = createAction<IAccountChangeTabPayload>(ActionTypes.ACCOUNT_CHANEGE_TAB);
export const ordersHistoryRequest = createAction(ActionTypes.ORDERS_HISTORY_REQUEST);
export const ordersHistorySuccess = createAction<IOrdersHistorySuccessPayload>(ActionTypes.ORDERS_HISTORY_SUCCESS);
export const customerClubFetchRequest = createAction(ActionTypes.CUSTOMER_CLUB_FETCH_REQUEST);
export const customerClubFetchSuccess = createAction<ICustomerClubFetchSuccessPayload>(ActionTypes.CUSTOMER_CLUB_FETCH_SUCCESS);

// Personal Identity Number
export const personalIdentityNumberFetchRequest = createAction<IPersonalIdentityNumberFetchRequestPayload>(ActionTypes.PERSONAL_IDENTITY_NUMBER_FETCH_REQUEST);
export const personalIdentityNumberFetchSuccess = createAction<IPersonalIdentityNumberFetchSuccessPayload>(ActionTypes.PERSONAL_IDENTITY_NUMBER_FETCH_SUCCESS);

// Campaign
export const campaignSiteChangeRequest = createAction<ICampaignSiteChangeRequestPayload>(ActionTypes.CAMPAIGN_SITE_CHANGE_REQUEST);
export const campaignSiteChangeSuccess = createAction<ICampaignSiteChangeSuccessPayload>(ActionTypes.CAMPAIGN_SITE_CHANGE_SUCCESS);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/reducers/checkout.ts